/**
 * Crisp live chat, with the merchant's identity attached.
 *
 * WHAT THIS FIXES. The Shopify app builds its Crisp payload in Blade, because
 * Laravel serves its dashboard. This app cannot: manage.zonstaff.com is a
 * static index.html on a different origin from the API, and a Wix merchant is
 * redirected straight to it — so the Blade snippet in the universal backend
 * (resources/views/universal_dashboard.blade.php, served only at GET /) has
 * never executed for a Wix, Square, Clover or AppSumo merchant. Every support
 * conversation from them has arrived anonymous. This is what identifies them.
 *
 * WHY THIS IS NOT AN INLINE SCRIPT IN index.html, which is how the Shopify app
 * does it:
 *
 *   - CRISP_TOKEN_ID must be set BEFORE l.js is injected; Crisp reads it once
 *     at boot and ignores it afterwards. That means holding back the injection
 *     until the identity lookup returns — so the injection has to be ours
 *     either way, and running from the bundle costs nothing extra.
 *   - An inline script cannot read import.meta.env, so the API base would have
 *     to be hardcoded in index.html, duplicating axiosClient's fallback.
 *   - Module initialisation still runs before any React effect, so the Wix
 *     ?token= is readable here (see authToken) — which is the one ordering
 *     constraint an inline script was buying us.
 *
 * DO NOT port the Shopify partial's App Bridge wait. It polls for
 * `window.shopify.idToken`, and shims/shopifyGlobal.js installs a stub
 * window.shopify with no idToken — the guard would find the object truthy,
 * poll out the full timeout and boot untokenised on every single load.
 */

import { APP_LOGO } from './components/ui/appBrand';

// Same tolerant resolution as components/axiosClient.jsx, so a staging build
// does not quietly ask production who the merchant is.
const API_ROOT = (import.meta.env.VITE_API_BASE_URL || 'https://api.zonstaff.com').replace(/\/+$/, '');
const API_BASE = /\/api$/.test(API_ROOT) ? API_ROOT : `${API_ROOT}/api`;

const WEBSITE_ID = import.meta.env.VITE_CRISP_WEBSITE_ID || '8305e843-f408-4c73-8b4d-c65666845987';

/**
 * The avatar for Wix merchants (owner, 2026-10-14), so support can tell a Wix
 * conversation at a glance. Everyone else keeps APP_LOGO (see inject()).
 */
const WIX_AVATAR = 'https://cdn.shopify.com/s/files/1/0942/5089/3625/files/ZON_FOr_WIx.png?v=1791982575';

/** The `platform` row of the identity's session data ('wix', 'square', ...). */
function platformOf(identity) {
  const row = Array.isArray(identity?.data) ? identity.data.find((r) => Array.isArray(r) && r[0] === 'platform') : null;
  return row ? String(row[1] || '').toLowerCase() : '';
}

/** A hung lookup must never cost us the chatbox. */
const IDENTITY_TIMEOUT_MS = 3000;

let injected = false;
let identifying = null;

/**
 * Crisp replays anything queued into this array once l.js initialises, so it
 * is safe to push before boot — and it must exist before l.js runs.
 */
function queue() {
  if (!Array.isArray(window.$crisp) && typeof window.$crisp !== 'object') {
    window.$crisp = [];
  }
  return window.$crisp || (window.$crisp = []);
}

/**
 * Inject l.js exactly once. Anything that must precede boot goes before this.
 *
 * The avatar is pushed from in here rather than from identifyCrisp so that it
 * lands on EVERY boot path - identified, unidentified, and the on-demand boot
 * from openChat - without three copies of the same line. It does not depend on
 * who the merchant is, so there is nothing to wait for.
 *
 * APP_LOGO is the app icon: the same constant the sidebar and the favicon
 * already use, and the same image the Shopify app sets. `Auth::user()->avatar`
 * over there is not a column, so its `??` fallback is what actually renders -
 * this is that image, from the one place this app already keeps it.
 */
function inject() {
  if (injected) return;
  injected = true;

  const q = queue();
  window.CRISP_WEBSITE_ID = WEBSITE_ID;

  if (APP_LOGO) q.push(['set', 'user:avatar', [APP_LOGO]]);

  const s = document.createElement('script');
  s.src = 'https://client.crisp.chat/l.js';
  s.async = 1;
  document.head.appendChild(s);
}

/**
 * Has the SDK actually arrived?
 *
 * NOT `if (window.$crisp)`. We set it to a plain array above, before l.js is
 * even requested, so that check is true from the first line and stays true
 * forever — including when an ad blocker stops client.crisp.chat loading. Once
 * the real SDK boots it replaces the array with its own object, which exposes
 * `is` as a function. That is the honest signal.
 */
export const crispLoaded = () => typeof window.$crisp?.is === 'function';

/**
 * The bearer token for this session.
 *
 * ORDER MATTERS. On a Wix merchant's first open the token exists ONLY in the
 * query string: WixEmbedded writes it to storage and strips the URL, both
 * inside a React effect that has not run when this module initialises. Reading
 * storage first would find nothing and the merchant would go unidentified for
 * the whole visit.
 */
function authToken() {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('token');
    if (fromUrl) return fromUrl;
  } catch {
    /* Malformed URL; fall through to storage. */
  }

  try {
    return localStorage.getItem('authToken') || sessionStorage.getItem('authToken') || null;
  } catch {
    // Private mode or blocked storage.
    return null;
  }
}

/**
 * Identify the signed-in merchant and boot the chatbox.
 *
 * Safe to call more than once — the lookup is memoised and l.js is injected
 * once. Called at module load and again after login, because the login screen
 * is a client-side transition: nothing reloads index.html between the two.
 *
 * Returns without booting when nobody is signed in. The login screen is the
 * only unauthenticated surface on this origin, and a visitor there has no
 * account for support to look up — booting would mint an anonymous Crisp
 * contact per visit. openChat() still boots on demand if they click through to
 * support from somewhere.
 */
export function identifyCrisp() {
  if (identifying) return identifying;

  const token = authToken();
  if (!token) return Promise.resolve(false);

  // HARD CEILING, independent of the request. The abort below is the tidy
  // path, but it only works if fetch honours the signal; this fires whatever
  // happens, so a dead lookup can never cost us the chatbox. Same belt and
  // braces as the Shopify partial. inject() is idempotent, so whichever lands
  // first wins and the other is a no-op - the only cost of losing the race is
  // that CRISP_TOKEN_ID arrives too late to be read, which degrades to a
  // conversation without history rather than no conversation.
  setTimeout(inject, IDENTITY_TIMEOUT_MS);

  // The returned promise must always settle. A fetch that ignores its abort
  // signal would otherwise leave it pending forever, and any caller that
  // awaited it would hang with it. Nothing awaits it today; this makes sure
  // nothing can be broken by starting to.
  const ceiling = new Promise((resolve) => setTimeout(() => resolve(false), IDENTITY_TIMEOUT_MS + 250));

  identifying = Promise.race([ceiling, (async () => {
    let identity = null;

    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), IDENTITY_TIMEOUT_MS);

      const response = await fetch(`${API_BASE}/crisp-identity`, {
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
        signal: controller.signal,
      });

      clearTimeout(timer);

      if (response.ok) {
        const body = await response.json();
        if (body && body.success) identity = body;
      }
    } catch {
      /* Aborted, offline, or the backend predates this endpoint. */
    }

    // Boot either way. A merchant who cannot reach support is a worse outcome
    // than one whose conversation is missing its context panel — and this
    // frontend may ship before the endpoint exists.
    if (!identity) {
      inject();
      return false;
    }

    // BEFORE inject(): Crisp reads CRISP_TOKEN_ID once, at boot. This is what
    // keeps a returning merchant's history, and what stops the next merchant
    // to sign in on a shared browser from inheriting the previous one's
    // thread — Crisp's own session id lives in localStorage and would
    // otherwise be reused by whoever is sitting there.
    if (identity.token) window.CRISP_TOKEN_ID = identity.token;

    const q = queue();
    q.push(['config', 'color:theme', ['black']]);

    // NO position:reverse. The Shopify app sets it, which puts the launcher
    // bottom-LEFT — directly over this app's sidebar and its Logout row. That
    // overlap is why the chatbox was pulled from this app in the first place.

    inject();

    // After inject(): it queues APP_LOGO, and Crisp applies the queue in order,
    // so this one wins.
    if (platformOf(identity) === 'wix') q.push(['set', 'user:avatar', [WIX_AVATAR]]);

    if (identity.nickname) q.push(['set', 'user:nickname', [String(identity.nickname)]]);

    // Only ever a real address. The backend filters the synthetic
    // wix_<id>@no-email.com / square_<id>@no-email.com logins out, because
    // Crisp merges contacts by email and those would mint junk contacts on a
    // domain that cannot receive mail.
    if (identity.email) q.push(['set', 'user:email', [String(identity.email)]]);

    if (Array.isArray(identity.data) && identity.data.length) {
      q.push(['set', 'session:data', [identity.data]]);
    }

    return true;
  })()]);

  return identifying;
}

/**
 * Open the chatbox, booting it first if it is not up yet.
 *
 * `subject` is pre-filled as the visitor's first message, which is how the
 * Shopify app's "Learn more" and bonus-seat buttons work.
 *
 * Returns a promise for whether Crisp actually came up, so the caller can fall
 * back to email when it is blocked.
 */
export function openChat(subject) {
  inject();

  const q = queue();
  q.push(['do', 'chat:open']);
  if (typeof subject === 'string' && subject.trim()) {
    q.push(['do', 'message:send', ['text', subject.trim()]]);
  }

  if (crispLoaded()) return Promise.resolve(true);

  // Give a slow connection a moment before the caller gives up on us, so we do
  // not hijack a click Crisp was a beat away from honouring.
  return new Promise((resolve) => {
    setTimeout(() => resolve(crispLoaded()), 1500);
  });
}

/**
 * End this merchant's chat session.
 *
 * MUST run on logout. Crisp keeps its session id in localStorage, so without
 * this the next merchant to sign in on the same browser resumes the previous
 * merchant's conversation — and their session:data is written onto the
 * previous merchant's contact record. Clearing our own authToken does nothing
 * about Crisp's.
 */
export function resetCrispSession() {
  try {
    if (crispLoaded()) window.$crisp.push(['do', 'session:reset']);
  } catch {
    /* Never let a support widget break signing out. */
  }

  // So a subsequent login re-runs the lookup rather than replaying the cached
  // promise for the account that just left.
  identifying = null;
}
